import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronLeft, ChevronRight, Pause, Play, Volume2, VolumeX, MapPin, Mail, Phone } from "lucide-react";
import { resolveMedia, getNews } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

function getIcon(name, props = {}) {
  if (!name) return null;
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp aria-hidden {...props} />;
}

function videoPosterUrl(media) {
  if (!media) return "";
  if (media.poster_url) return media.poster_url;
  const videoUrl = media.video_url || media.url || "";
  return videoUrl.replace(/\.(mp4|webm|mov)(?=([?#]|$))/i, ".poster.webp");
}

// Renders an <a> for external/protocol URLs (http, mailto, tel, wa.me), otherwise a router <Link>.
// Internal paths are prefixed with /en when the UI language is English.
function SmartLink({ to, children, ...rest }) {
  const { lp } = useLang();
  const url = to || "/kontakt/";
  const isProtocol = /^(https?:|mailto:|tel:)/i.test(url) || url.startsWith("wa.me");
  if (isProtocol) {
    const href = url.startsWith("wa.me") ? `https://${url}` : url;
    const isHttp = /^https?:/i.test(href);
    return (
      <a href={href} {...(isHttp ? { target: "_blank", rel: "noopener noreferrer" } : {})} {...rest}>
        {children}
      </a>
    );
  }
  return <Link to={lp(url)} {...rest}>{children}</Link>;
}

function CTAList({ ctas = [] }) {
  if (!ctas?.length) return null;
  return (
    <div className="flex flex-wrap gap-3 mt-6">
      {ctas.map((c, i) => (
        <SmartLink
          key={i}
          to={c.url || "/kontakt/"}
          data-testid={`cta-${c.variant || "primary"}-${i}`}
          className={c.variant === "secondary" || c.variant === "ghost" ? "btn-secondary" : "btn-primary"}
        >
          {c.label}
        </SmartLink>
      ))}
    </div>
  );
}

// ============== Stats Counter (Preventics-style) ==============
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    const o = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); o.disconnect(); } },
      { threshold }
    );
    o.observe(ref.current);
    return () => o.disconnect();
  }, [inView, threshold]);
  return [ref, inView];
}

function CountUp({ value, duration = 1400 }) {
  const m = String(value).match(/^(\d+)(.*)$/);
  const target = m ? parseInt(m[1], 10) : 0;
  const suffix = m ? m[2] : value;
  const [ref, inView] = useInView();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setN(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return <span ref={ref}>{m ? `${n}${suffix}` : value}</span>;
}

function StatsCounter({ s }) {
  const stats = s.extra?.stats || [];
  return (
    <section data-section-type="stats_counter" className="py-16 md:py-24 bg-surface">
      <div className="container-x">
        {(s.eyebrow_de || s.title_de) && (
          <div className="max-w-3xl mb-10 md:mb-14">
            {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
            {s.title_de && <h2 className="font-display text-display-lg text-navy-dark tracking-tight">{s.title_de}</h2>}
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {stats.map((st, i) => (
            <div key={i} className="neu-card p-7 md:p-8 text-center" data-testid={`stat-${i}`}>
              <div className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-navy-dark leading-none tracking-tight">
                <CountUp value={st.value} />
              </div>
              <div className="mt-3 text-base md:text-lg font-semibold text-navy">{st.label}</div>
              {st.note && <div className="mt-1 text-sm text-ink-muted">{st.note}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== Story List (em-dash separated) ==============
function StoryList({ s }) {
  const lines = s.extra?.lines || [];
  const [ref, inView] = useInView(0.1);
  return (
    <section data-section-type="story_list" className="py-20 md:py-32 bg-navy-dark text-white relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-30" />
      <div className="absolute -right-32 top-20 w-96 h-96 rounded-full bg-brandred/15 blur-[120px]" />
      <div className="absolute -left-40 bottom-10 w-[28rem] h-[28rem] rounded-full bg-navy/40 blur-[140px]" />
      <div className="container-x relative max-w-4xl" ref={ref}>
        {s.eyebrow_de && <div className="eyebrow text-white/70 mb-4">{s.eyebrow_de}</div>}
        {s.title_de && (
          <h2 className="font-display text-display-lg md:text-5xl lg:text-6xl font-extrabold text-white mb-10 md:mb-14 tracking-tight leading-[1.05]">
            {s.title_de}
          </h2>
        )}
        <ul className="space-y-6 md:space-y-8">
          {lines.map((line, i) => (
            <li
              key={i}
              className={`flex gap-4 md:gap-6 transition-all duration-700 ${inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}
              style={{ transitionDelay: `${i * 90}ms` }}
            >
              <span className="text-brandred font-extrabold text-2xl md:text-3xl leading-none select-none">—</span>
              <p className="text-lg md:text-xl text-white/90 leading-relaxed">{line}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ============== Image Gallery ==============
function ImageGallery({ s }) {
  const items = (s.media_items || []).filter((m) => m && m.url);
  if (!items.length) return null;
  const cols = s.extra?.columns || 3;
  const aspect = s.extra?.aspect || "square";
  const aspectCls = aspect === "video" ? "aspect-video" : aspect === "portrait" ? "aspect-[3/4]" : aspect === "wide" ? "aspect-[16/9]" : "aspect-square";
  const colCls = cols === 2 ? "grid-cols-1 sm:grid-cols-2" : cols === 4 ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
  return (
    <section data-section-type="image_gallery" className="py-16 md:py-24 bg-surface">
      <div className="container-x">
        {(s.eyebrow_de || s.title_de || s.body_de) && (
          <div className="max-w-3xl mb-10 md:mb-14">
            {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
            {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-4">{s.title_de}</h2>}
            {s.body_de && <p className="text-ink-muted text-lg leading-relaxed">{s.body_de}</p>}
          </div>
        )}
        <div className={`grid gap-5 md:gap-6 ${colCls}`}>
          {items.map((m, i) => (
            <figure key={m.id || i} className="group neu-card !p-2 overflow-hidden" data-testid={`gallery-img-${i}`}>
              <div className={`relative ${aspectCls} overflow-hidden rounded-2xl bg-surface-muted`}>
                <img
                  src={resolveMedia(m.url)}
                  alt={m.alt_text || ""}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {m.caption && (
                <figcaption className="px-3 py-2 text-xs text-ink-muted">{m.caption}</figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== Hero: Fullscreen Video Slider ==============
function HeroVideoSlider({ s }) {
  const items = (s.media_items || []).filter(Boolean);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (items.length < 2 || paused) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % items.length), 9000);
    return () => clearTimeout(t);
  }, [idx, items.length, paused]);

  const current = items[idx];
  const isVideo = current && (current.video_url || (current.url && /\.(mp4|webm|mov)$/i.test(current.url)));

  // Reset video state on slide change
  useEffect(() => { setVideoPlaying(true); }, [idx]);

  const togglePlayVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().then(() => setVideoPlaying(true)).catch(() => {}); }
    else { v.pause(); setVideoPlaying(false); }
  };

  return (
    <section
      data-testid="section-hero-video-slider"
      data-section-type="fullscreen_video_slider_hero"
      className="relative w-full min-h-[100svh] sm:min-h-[88vh] sm:aspect-video sm:max-h-[900px] overflow-hidden bg-navy-dark text-white isolate"
    >
      {/* Media layer */}
      <div className="absolute inset-0">
        {current && isVideo ? (
          <video
            key={current.video_url || current.url}
            ref={videoRef}
            src={resolveMedia(current.video_url || current.url)}
            poster={resolveMedia(videoPosterUrl(current))}
            preload="none"
            autoPlay
            muted={muted}
            loop
            playsInline
            onPlay={() => setVideoPlaying(true)}
            onPause={() => setVideoPlaying(false)}
            className="absolute inset-0 w-full h-full object-cover"
            aria-label={current.alt_text || ""}
          />
        ) : current ? (
          <img
            src={resolveMedia(current.poster_url || current.url)}
            alt={current.alt_text || ""}
            className="absolute inset-0 w-full h-full object-cover animate-slow-pan"
          />
        ) : null}
        <div className="absolute inset-0" style={{ background: (() => {
          const ov = Math.min(100, Math.max(0, Number(s?.extra?.hero_overlay ?? 70))) / 100;
          const c = (a) => `rgba(0,30,64,${Math.max(0, Math.min(1, a)).toFixed(2)})`;
          return `linear-gradient(to top right, ${c(ov + 0.18)} 0%, ${c(ov)} 42%, ${c(ov - 0.4)} 100%)`;
        })() }} />
        <div className="absolute inset-0 grain" />
      </div>

      {/* Central Play overlay (only when video paused) */}
      {isVideo && !videoPlaying && (
        <button
          type="button"
          onClick={togglePlayVideo}
          aria-label="Video abspielen"
          data-testid="hero-play-overlay"
          className="absolute inset-0 z-10 flex items-center justify-center group/play"
        >
          <span className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-white/95 text-navy-dark flex items-center justify-center shadow-[0_10px_40px_rgba(0,0,0,0.4)] group-hover/play:scale-110 transition-transform">
            <Play className="h-9 w-9 md:h-10 md:w-10 ml-1.5 fill-current" />
          </span>
        </button>
      )}

      {/* Content layer */}
      <div className="container-x relative min-h-full flex flex-col justify-center py-24 md:py-28 z-10">
        <div className="max-w-3xl animate-fade-up">
          {s.eyebrow_de && <div className="eyebrow text-white/85 mb-3 md:mb-4" data-testid="hero-eyebrow">{s.eyebrow_de}</div>}
          {s.h_tag === "h1" ? (
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-3 md:mb-4 tracking-tight leading-[1.08]" data-testid="hero-h1">{s.title_de}</h1>
          ) : (
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-3 md:mb-4 tracking-tight leading-[1.08]">{s.title_de}</h2>
          )}
          {s.subtitle_de && <p className="text-base sm:text-lg md:text-xl text-white/90 mb-3">{s.subtitle_de}</p>}
          {s.body_de && <p className="text-base md:text-lg text-white/80 max-w-2xl leading-relaxed">{s.body_de}</p>}
          <CTAList ctas={s.ctas} />
        </div>

        {/* Controls */}
        <div className="absolute right-4 md:right-8 bottom-6 flex items-center gap-2">
          {isVideo && (
            <>
              <button
                onClick={togglePlayVideo}
                className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center"
                aria-label={videoPlaying ? "Video pausieren" : "Video abspielen"}
                data-testid="hero-toggle-play"
              >
                {videoPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
              </button>
              <button
                onClick={() => setMuted((m) => !m)}
                className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center"
                aria-label={muted ? "Ton einschalten" : "Ton ausschalten"}
                data-testid="hero-toggle-sound"
              >
                {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
            </>
          )}
          {items.length > 1 && (
            <>
              <button
                onClick={() => setPaused((p) => !p)}
                className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center"
                aria-label={paused ? "Slider fortsetzen" : "Slider pausieren"}
                data-testid="hero-toggle-pause"
              >
                {paused ? <Play className="h-5 w-5 ml-0.5" /> : <Pause className="h-5 w-5" />}
              </button>
              <button
                onClick={() => setIdx((idx - 1 + items.length) % items.length)}
                className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center"
                aria-label="Vorheriges Slide"
                data-testid="hero-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIdx((idx + 1) % items.length)}
                className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center"
                aria-label="Nächstes Slide"
                data-testid="hero-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {items.length > 1 && (
          <div className="absolute left-4 md:left-8 bottom-7 flex items-center gap-2" data-testid="hero-dots">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-10 bg-white" : "w-4 bg-white/40 hover:bg-white/60"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ============== Split Text Media ==============
function SplitTextMedia({ s }) {
  const media = (s.media_items || [])[0];
  const reverse = s.extra?.media_position === "left";
  const textOnly = s.extra?.text_only || !media;
  const dark = s.design_variant === "muted";

  return (
    <section
      data-section-type="split_text_media"
      className={`relative py-16 md:py-24 ${dark ? "bg-surface-muted" : "bg-surface"}`}
    >
      <div className={`container-x grid gap-10 lg:gap-16 ${textOnly ? "max-w-4xl" : "lg:grid-cols-2 items-center"}`}>
        <div className={`${reverse ? "lg:order-2" : ""}`}>
          {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
          {s.title_de && (
            s.h_tag === "h1"
              ? <h1 className="font-display text-display-lg text-navy-dark mb-4">{s.title_de}</h1>
              : <h2 className="font-display text-display-lg text-navy-dark mb-4">{s.title_de}</h2>
          )}
          {s.subtitle_de && <p className="text-lg md:text-xl text-navy mb-4">{s.subtitle_de}</p>}
          {s.body_de && (
            <div className="prose-medical text-ink leading-relaxed">
              {s.body_de.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
            </div>
          )}
          <CTAList ctas={s.ctas} />
        </div>
        {!textOnly && media && (
          <div className={`${reverse ? "lg:order-1" : ""} relative`}>
            <div className="relative overflow-hidden rounded-3xl neu-card !p-2 bg-white">
              {(media.video_url || /\.(mp4|webm|mov)$/i.test(media.url || "")) ? (
                <video
                  src={resolveMedia(media.video_url || media.url)}
                  poster={resolveMedia(videoPosterUrl(media))}
                  preload="none"
                  autoPlay muted loop playsInline
                  className="w-full h-full object-cover aspect-[4/3] rounded-2xl"
                />
              ) : (
                <img
                  src={resolveMedia(media.url)}
                  alt={media.alt_text || s.title_de || ""}
                  loading="lazy"
                  className="w-full h-full object-cover aspect-[4/3] rounded-2xl"
                />
              )}
            </div>
            <div className="absolute -z-10 -bottom-4 -right-4 w-32 h-32 rounded-full bg-brandred/10 blur-2xl" />
          </div>
        )}
      </div>
    </section>
  );
}

// ============== Card Grid ==============
function CardGrid({ s }) {
  const cols = s.extra?.columns || 3;
  const colCls = cols === 2 ? "md:grid-cols-2" : cols === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3";
  const dark = s.design_variant === "muted";

  return (
    <section
      data-section-type="card_grid"
      className={`py-16 md:py-24 ${dark ? "bg-surface-muted" : "bg-surface"}`}
    >
      <div className="container-x">
        <div className="max-w-3xl mb-10 md:mb-14">
          {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
          {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-4">{s.title_de}</h2>}
          {s.body_de && <p className="text-ink-muted text-lg leading-relaxed">{s.body_de}</p>}
        </div>
        <div className={`grid grid-cols-1 ${colCls} gap-6 md:gap-8`}>
          {(s.cards || []).map((c, i) => (
            <article
              key={i}
              id={c.anchor || undefined}
              className="group neu-card p-8 md:p-10 flex flex-col scroll-mt-32"
              data-testid={`card-${i}`}
            >
              <div className="h-20 w-20 md:h-24 md:w-24 neu-icon-blue mb-6 group-hover:scale-105 transition-transform">
                {getIcon(c.icon, { className: "h-10 w-10 md:h-12 md:w-12 stroke-[1.6]" })}
              </div>
              {c.title && <h3 className="text-2xl md:text-[26px] font-bold text-navy-dark mb-3 leading-tight">{c.title}</h3>}
              {c.text && <p className="text-ink-muted leading-relaxed text-[17px] md:text-lg">{c.text}</p>}
              {c.url && (
                <SmartLink to={c.url} className="mt-6 inline-flex items-center gap-2 text-brandred font-semibold text-base hover:gap-3 transition-all">
                  {c.cta_label || "Mehr erfahren"} <ChevronRight className="h-5 w-5" />
                </SmartLink>
              )}
            </article>
          ))}
        </div>
        <CTAList ctas={s.ctas} />
      </div>
    </section>
  );
}

// ============== Timeline Process ==============
function TimelineProcess({ s }) {
  return (
    <section data-section-type="timeline_process" className="py-16 md:py-24 bg-surface">
      <div className="container-x">
        <div className="max-w-3xl mb-12">
          {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
          {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-4">{s.title_de}</h2>}
          {s.body_de && <p className="text-ink-muted text-lg">{s.body_de}</p>}
        </div>
        <ol className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {(s.timeline_steps || []).map((st, i) => (
            <li
              key={i}
              className="relative neu-card p-8"
              data-testid={`timeline-step-${i}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="h-10 px-4 inline-flex items-center neu-pill text-navy text-sm font-bold tabular-nums">
                  {st.number || String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-10 w-10 neu-icon text-navy">
                  {getIcon(st.icon, { className: "h-5 w-5" })}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-navy-dark mb-2">{st.title}</h3>
              {st.text && <p className="text-ink-muted leading-relaxed">{st.text}</p>}
            </li>
          ))}
        </ol>
        <CTAList ctas={s.ctas} />
      </div>
    </section>
  );
}

// ============== Statement CTA ==============
function StatementCTA({ s }) {
  const variant = s.design_variant;
  const isDark = variant === "dark";
  const isMedical = variant === "medical";

  if (isDark) {
    return (
      <section
        data-section-type="statement_cta"
        className="relative py-20 md:py-28 bg-navy-dark text-white overflow-hidden"
      >
        <div className="absolute inset-0 grain opacity-30" />
        <div className="container-x relative text-center max-w-3xl mx-auto">
          {s.title_de && <h2 className="font-display text-display-xl text-white mb-5">{s.title_de}</h2>}
          {s.subtitle_de && <p className="text-xl text-white/85 mb-6">{s.subtitle_de}</p>}
          {s.body_de && <p className="text-white/75">{s.body_de}</p>}
          <div className="flex flex-wrap gap-3 mt-8 justify-center">
            {(s.ctas || []).map((c, i) => (
              <SmartLink key={i} to={c.url || "/kontakt/"} className={c.variant === "secondary" ? "btn-ghost" : "btn-primary"}>
                {c.label}
              </SmartLink>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (isMedical) {
    return (
      <section data-section-type="statement_cta" className="py-16 md:py-24 bg-surface">
        <div className="container-x">
          <div className="relative neu-card !p-10 md:!p-16 overflow-hidden">
            <div className="absolute -right-12 -bottom-12 w-72 h-72 rounded-full bg-brandred/10 blur-3xl" />
            <div className="absolute -left-12 -top-12 w-64 h-64 rounded-full bg-navy/10 blur-3xl" />
            <div className="relative max-w-3xl">
              {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-3">{s.title_de}</h2>}
              {s.subtitle_de && <p className="text-lg text-navy/80 mb-4 font-medium">{s.subtitle_de}</p>}
              {s.body_de && <p className="text-ink-muted mb-6">{s.body_de}</p>}
              <div className="flex flex-wrap gap-3">
                {(s.ctas || []).map((c, i) => (
                  <SmartLink key={i} to={c.url || "/kontakt/"} className={c.variant === "secondary" ? "btn-secondary" : "btn-primary"}>
                    {c.label}
                  </SmartLink>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-section-type="statement_cta" className="py-16 md:py-24 bg-surface-muted">
      <div className="container-x text-center max-w-3xl mx-auto">
        {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-4">{s.title_de}</h2>}
        {s.subtitle_de && <p className="text-xl text-ink-muted mb-6">{s.subtitle_de}</p>}
        {s.body_de && <p className="text-ink-muted">{s.body_de}</p>}
        <div className="flex flex-wrap gap-3 mt-8 justify-center">
          {(s.ctas || []).map((c, i) => (
            <SmartLink key={i} to={c.url || "/kontakt/"} className={c.variant === "secondary" ? "btn-secondary" : "btn-primary"}>
              {c.label}
            </SmartLink>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== FAQ Accordion ==============
function FAQAccordion({ s }) {
  const items = s.faq_items || [];
  return (
    <section data-section-type="faq_accordion" className="py-16 md:py-24 bg-surface">
      <div className="container-x max-w-4xl">
        {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
        {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-8">{s.title_de}</h2>}
        <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
          {items.map((it, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="neu-card !p-0 px-6 border-0"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-navy-dark hover:no-underline py-5">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-ink-muted leading-relaxed pb-5">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ============== Location Cards ==============
function LocationCards({ s, locations = [] }) {
  const { lang } = useLang();
  return (
    <section
      data-section-type="location_cards"
      className={`py-16 md:py-24 ${s.design_variant === "muted" ? "bg-surface-muted" : "bg-surface"}`}
    >
      <div className="container-x">
        <div className="max-w-3xl mb-10">
          {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
          {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-4">{s.title_de}</h2>}
          {s.body_de && <p className="text-ink-muted text-lg">{s.body_de}</p>}
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {locations.map((loc) => (
            <article key={loc.id} className="neu-card overflow-hidden !p-0" data-testid={`location-${loc.key}`}>
              {loc.image_url && (
                <div className="aspect-[16/9] overflow-hidden bg-surface-muted">
                  <img src={resolveMedia(loc.image_url)} alt={loc.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-8">
                <div className="flex items-start gap-3 mb-3">
                  <div className="h-10 w-10 neu-icon text-brandred shrink-0">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy-dark">{loc.name}</h3>
                    {loc.is_primary && (
                      <span className="inline-block mt-1 text-xs font-medium uppercase tracking-wider text-brandred">
                        {lang === "en" ? "Main location" : "Hauptstandort"}
                      </span>
                    )}
                  </div>
                </div>
                <address className="not-italic text-ink-muted mb-4">
                  {loc.street}<br />{loc.zip} {loc.city}
                </address>
                {loc.description_de && <p className="text-ink-muted mb-4 text-sm leading-relaxed">{loc.description_de}</p>}
                <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  {loc.phone && (
                    <a href={`tel:${loc.phone.replace(/\s/g,"")}`} className="inline-flex items-center gap-2 text-navy hover:text-brandred">
                      <Phone className="h-4 w-4" /> {loc.phone}
                    </a>
                  )}
                  {loc.email && (
                    <a href={`mailto:${loc.email}`} className="inline-flex items-center gap-2 text-navy hover:text-brandred">
                      <Mail className="h-4 w-4" /> {loc.email}
                    </a>
                  )}
                </div>
                <SmartLink to="/kontakt/" className="btn-primary mt-5 !text-sm" data-testid={`location-cta-${loc.key}`}>
                  {lang === "en" ? `Request appointment in ${loc.city}` : `Termin in ${loc.city} anfragen`}
                </SmartLink>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== Doctor Profile ==============
function DoctorProfile({ s }) {
  const media = (s.media_items || [])[0];
  return (
    <section data-section-type="doctor_profile" className="py-16 md:py-24 bg-white">
      <div className="container-x grid gap-10 lg:grid-cols-5 items-start">
        {media && (
          <div className="lg:col-span-2 relative">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-outline">
              <img src={resolveMedia(media.url)} alt={media.alt_text || ""} className="w-full h-full object-cover" />
            </div>
            <div className="absolute -z-10 -top-4 -left-4 w-24 h-24 rounded-full bg-navy/10 blur-2xl" />
          </div>
        )}
        <div className="lg:col-span-3">
          {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
          {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-3">{s.title_de}</h2>}
          {s.subtitle_de && <p className="text-xl text-navy mb-5">{s.subtitle_de}</p>}
          {s.body_de && (
            <div className="prose-medical text-ink leading-relaxed">
              {s.body_de.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ============== News Grid ==============
function NewsGrid({ s }) {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { getNews({ limit: s.extra?.limit || 12, lang }).then(setItems).catch(() => {}); }, [s.extra?.limit, lang]);
  return (
    <section data-section-type="news_grid" className="py-16 md:py-24 bg-surface">
      <div className="container-x">
        <div className="max-w-3xl mb-10">
          {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
          {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-4">{s.title_de}</h2>}
          {s.body_de && <p className="text-ink-muted text-lg">{s.body_de}</p>}
        </div>
        {items.length === 0 ? (
          <p className="text-ink-muted">{lang === "en" ? "No news available at the moment." : "Aktuell sind keine News verfügbar."}</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((n) => (
              <SmartLink
                to={`/news/${n.slug}`}
                key={n.id}
                className="group neu-card overflow-hidden !p-0 flex flex-col"
                data-testid={`news-card-${n.slug}`}
              >
                {(n.hero_image_url || n.video_poster_url) && (
                  <div className="aspect-[16/10] overflow-hidden bg-surface-muted">
                    <img
                      src={resolveMedia(n.hero_image_url || n.video_poster_url)}
                      alt={n.title_de}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-xs text-ink-muted mb-2">
                    {n.category && <span className="uppercase tracking-wider font-semibold">{n.category}</span>}
                    <span>{(n.published_at || "").slice(0, 10)}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-navy-dark group-hover:text-brandred transition-colors mb-2">{n.title_de}</h3>
                  {n.teaser_de && <p className="text-ink-muted leading-relaxed line-clamp-3">{n.teaser_de}</p>}
                  <span className="inline-flex items-center gap-2 mt-4 text-brandred font-semibold text-sm">
                    {t("allNews") === "All news" ? "Read more" : "Weiterlesen"} <ChevronRight className="h-4 w-4" />
                  </span>
                </div>
              </SmartLink>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ============== Video Only ==============
// ============== Video Slider (multi-video, with sound, swipeable) ==============
function VideoSliderSection({ s }) {
  const items = (s.media_items || []).filter(Boolean);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const videoRef = useRef(null);
  const touchX = useRef(null);
  const squareMobile = s.extra?.mobile_square !== false;

  // reset on slide change
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setPlaying(false);
    setStarted(false);
  }, [idx]);

  if (items.length === 0) return null;
  const current = items[idx];

  const playCurrent = () => {
    const v = videoRef.current;
    if (!v) return;
    const url = resolveMedia(current.video_url || current.url);
    if (!v.getAttribute("src")) {
      v.src = url && !url.includes("#") ? `${url}#t=0.1` : url;
      v.load();
    }
    setStarted(true);
    v.play().then(() => setPlaying(true)).catch(() => {});
  };
  const go = (delta) => setIdx((i) => (i + delta + items.length) % items.length);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 50) go(dx > 0 ? -1 : 1);
  };
  const bg = s.design_variant === "dark" ? "bg-navy-dark text-white" : "bg-surface";

  return (
    <section data-section-type="video_slider" data-testid="section-video-slider" className={`relative py-14 md:py-20 ${bg}`}>
      <div className="container-x">
        {(s.eyebrow_de || s.title_de) && (
          <div className="max-w-3xl mb-8">
            {s.eyebrow_de && <div className={`eyebrow mb-3 ${s.design_variant==="dark"?"text-white/70":""}`}>{s.eyebrow_de}</div>}
            {s.title_de && <h2 className={`font-display text-display-lg ${s.design_variant==="dark"?"text-white":"text-navy-dark"} mb-3`}>{s.title_de}</h2>}
            {s.body_de && <p className={`text-lg ${s.design_variant==="dark"?"text-white/80":"text-ink-muted"}`}>{s.body_de}</p>}
          </div>
        )}

        <div
          className="relative rounded-2xl overflow-hidden bg-black mx-auto max-w-5xl"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className={`relative ${squareMobile ? "aspect-square sm:aspect-video" : "aspect-video"} max-h-[80vh]`}>
            <video
              ref={videoRef}
              key={current.video_url || current.url}
              src={started ? (() => {
                const u = resolveMedia(current.video_url || current.url);
                return u && !u.includes('#') ? `${u}#t=0.1` : u;
              })() : undefined}
              poster={resolveMedia(videoPosterUrl(current))}
              controls={started}
              playsInline
              preload="none"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              className={`absolute inset-0 w-full h-full ${squareMobile ? "object-cover sm:object-contain" : "object-contain"} bg-black`}
              data-testid={`video-slider-video-${idx}`}
            />
            {!started && (current.video_url || current.url) && (
              <button
                type="button"
                onClick={playCurrent}
                aria-label="Video abspielen"
                data-testid="video-slider-play-overlay"
                className="absolute inset-x-0 top-[10%] bottom-auto z-10 flex items-start justify-center group/play"
              >
                <span className="h-16 w-16 md:h-[77px] md:w-[77px] rounded-full bg-white/95 text-navy-dark flex items-center justify-center shadow-[0_10px_40px_rgba(0,0,0,0.45)] group-hover/play:scale-110 transition-transform">
                  <Play className="h-7 w-7 md:h-9 md:w-9 ml-1 fill-current" />
                </span>
              </button>
            )}
          </div>
          {current.caption && false && (
            <div className="absolute left-3 bottom-16 sm:bottom-20 px-3 py-1.5 bg-black/55 text-white text-sm rounded-md backdrop-blur" data-testid="video-slider-caption">
              {current.caption}
            </div>
          )}
          {items.length > 1 && (
            <>
              <button onClick={() => go(-1)} className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center backdrop-blur" aria-label="Vorheriges Video" data-testid="video-slider-prev"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={() => go(1)} className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center backdrop-blur" aria-label="Nächstes Video" data-testid="video-slider-next"><ChevronRight className="h-5 w-5" /></button>
            </>
          )}
        </div>

        {items.length > 1 && (
          <div className="mt-5 flex flex-col items-center gap-3">
            <div className="flex gap-1.5" role="tablist" aria-label="Video-Auswahl">
              {items.map((_, i) => (
                <button key={i} role="tab" aria-selected={i === idx} aria-label={`Video ${i + 1} von ${items.length}`} onClick={() => setIdx(i)} data-testid={`video-slider-dot-${i}`} className={`h-2.5 rounded-full transition-all ${i === idx ? "w-8 bg-brandred" : "w-2.5 bg-navy/25 hover:bg-navy/50"}`} />
              ))}
            </div>
            <div className="hidden md:flex gap-2 overflow-x-auto max-w-full px-2 pb-1" data-testid="video-slider-thumbs">
              {items.map((m, i) => (
                <button key={i} onClick={() => setIdx(i)} className={`relative h-16 w-28 rounded-md overflow-hidden border-2 transition shrink-0 ${i === idx ? "border-brandred" : "border-transparent hover:border-navy/50"}`} aria-label={`Zu Video ${i + 1} springen`}>
                  {(m.poster_url || m.video_url || m.url) ? (
                    <img
                      src={resolveMedia(videoPosterUrl(m))}
                      alt={m.alt_text || ""}
                      loading="lazy"
                      className="w-full h-full object-cover bg-black"
                    />
                  ) : (
                    <div className="w-full h-full bg-navy-dark/80 flex items-center justify-center text-white"><Play className="h-4 w-4" /></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function VideoOnlySection({ s }) {
  const media = (s.media_items || [])[0];
  if (!media) return null;
  const squareMobile = s.extra?.mobile_square !== false;
  return (
    <section data-section-type="video_only" className="relative w-full bg-black overflow-hidden" data-testid="section-video-only">
      <div className={`relative ${squareMobile ? "aspect-square sm:aspect-video" : "aspect-video"} max-h-[80vh]`}>
        <video
          src={resolveMedia(media.video_url || media.url)}
          poster={resolveMedia(videoPosterUrl(media))}
                  preload="none"
          autoPlay muted loop playsInline controls={s.extra?.show_controls !== false}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
      {(s.title_de || s.body_de) && (
        <div className="container-x py-6 text-center">
          {s.title_de && <h2 className="font-display text-2xl md:text-3xl text-white">{s.title_de}</h2>}
          {s.body_de && <p className="text-white/75 mt-2">{s.body_de}</p>}
        </div>
      )}
    </section>
  );
}

// ============== HTML / Legal Block ==============
function HtmlBlock({ s }) {
  return (
    <section data-section-type="html_block" className="py-12 md:py-16 bg-surface">
      <div className="container-x max-w-3xl">
        <div className="neu-card p-8 md:p-10 prose-medical">
          {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-6 mt-0">{s.title_de}</h2>}
          {(s.body_de || "").split("\n\n").map((para, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br />") }} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== Contact Form ==============
function ContactFormSection({ s, onSubmit }) {
  return (
    <section data-section-type="contact_form" className="py-16 md:py-24 bg-surface">
      <div className="container-x grid gap-10 lg:grid-cols-2">
        <div>
          {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
          {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-4">{s.title_de}</h2>}
          {s.body_de && <p className="text-ink-muted text-lg leading-relaxed">{s.body_de}</p>}
          <div className="mt-6 p-6 neu-inset text-sm text-ink-muted">
            <p>Bitte senden Sie nur Informationen, die für Ihre Anfrage erforderlich sind. Wir behandeln Ihre Daten vertraulich.</p>
          </div>
        </div>
        <div>{onSubmit}</div>
      </div>
    </section>
  );
}

// ============== Glossary List (alphabetical) ==============
function GlossaryList({ s }) {
  const { lang } = useLang();
  const terms = (s.extra?.terms || []).filter(t => t && t.term);
  const [q, setQ] = useState("");
  const featuredTerms = terms.filter(t => t.featured);
  // Group by first letter
  const filtered = terms.filter(t =>
    !q || t.term.toLowerCase().includes(q.toLowerCase()) || (t.definition || "").toLowerCase().includes(q.toLowerCase())
  );
  const groupedTerms = q ? filtered : filtered.filter(t => !t.featured);
  const groups = {};
  groupedTerms.forEach(t => {
    const letter = (t.term[0] || "#").toUpperCase();
    if (!groups[letter]) groups[letter] = [];
    groups[letter].push(t);
  });
  const letters = Object.keys(groups).sort();
  return (
    <section data-section-type="glossary_list" className="py-16 md:py-24 bg-surface">
      <div className="container-x max-w-5xl">
        {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-6">{s.title_de}</h2>}
        {!q && featuredTerms.length > 0 && (
          <div className="mb-12 rounded-3xl bg-navy-dark px-6 py-8 md:px-10 md:py-10 shadow-lg">
            <div className="eyebrow mb-3 !text-white/70">{lang === "en" ? "New at Preventum Diez" : "Neu bei Preventum Diez"}</div>
            <h2 className="font-display text-3xl md:text-4xl text-white font-extrabold mb-7">
              {lang === "en" ? "Additional services at a glance" : "Weitere Angebote auf einen Blick"}
            </h2>
            <dl className="grid gap-4 md:grid-cols-2">
              {featuredTerms.map((t, i) => (
                <div key={i} className="rounded-2xl bg-white p-6 shadow-sm" data-testid={`glossary-featured-${t.term}`}>
                  <dt className="text-xl font-semibold text-navy-dark mb-3">{t.term}</dt>
                  <dd className="text-ink-muted leading-relaxed text-[15px]">{t.definition}</dd>
                  {t.url && (
                    <SmartLink to={t.url} className="mt-4 inline-flex font-semibold text-navy hover:text-brandred transition-colors">
                      {t.link_label || (lang === "en" ? "Learn more" : "Weitere Informationen")} <span aria-hidden className="ml-1">→</span>
                    </SmartLink>
                  )}
                </div>
              ))}
            </dl>
          </div>
        )}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <input
            type="search"
            placeholder="Begriff suchen…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="glossary-search"
            className="w-full sm:w-72 h-11 px-4 rounded-xl border border-outline bg-white text-sm focus:outline-none focus:ring-2 focus:ring-navy/30"
          />
          {letters.length > 0 && (
            <div className="flex flex-wrap gap-1 text-xs">
              {letters.map(l => (
                <a key={l} href={`#glossar-${l}`} className="neu-pill !h-8 !px-3 text-navy font-bold hover:text-brandred">{l}</a>
              ))}
            </div>
          )}
        </div>
        {letters.length === 0 ? (
          <p className="text-ink-muted">Keine passenden Einträge gefunden.</p>
        ) : (
          letters.map(letter => (
            <div key={letter} id={`glossar-${letter}`} className="mb-10 scroll-mt-32">
              <h3 className="font-display text-3xl md:text-4xl text-brandred font-extrabold mb-5">{letter}</h3>
              <dl className="grid gap-4 md:grid-cols-2">
                {groups[letter].map((t, i) => (
                  <div key={i} className="neu-card p-6" data-testid={`glossary-term-${t.term}`}>
                    <dt className="text-lg font-semibold text-navy-dark mb-2">{t.term}</dt>
                    <dd className="text-ink-muted leading-relaxed text-[15px]">{t.definition}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ============== Custom HTML Block (admin can paste embed/animation HTML) ==============
function CustomHtmlEmbed({ s }) {
  const html = s.body_de || "";
  return (
    <section data-section-type="custom_html" className={`py-16 md:py-24 ${s.design_variant === "muted" ? "bg-surface-muted" : "bg-surface"}`}>
      <div className="container-x">
        {(s.eyebrow_de || s.title_de) && (
          <div className="max-w-3xl mb-8">
            {s.eyebrow_de && <div className="eyebrow mb-3">{s.eyebrow_de}</div>}
            {s.title_de && <h2 className="font-display text-display-lg text-navy-dark mb-3">{s.title_de}</h2>}
            {s.subtitle_de && <p className="text-ink-muted text-lg">{s.subtitle_de}</p>}
          </div>
        )}
        <div className="custom-html-embed" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </section>
  );
}

// ============== Router ==============
export default function SectionRenderer({ section, locations = [], contactForm = null }) {
  const s = section;
  switch (s.type) {
    case "fullscreen_video_slider_hero": return <HeroVideoSlider s={s} />;
    case "split_text_media": return <SplitTextMedia s={s} />;
    case "card_grid": return <CardGrid s={s} />;
    case "timeline_process": return <TimelineProcess s={s} />;
    case "statement_cta": return <StatementCTA s={s} />;
    case "faq_accordion": return <FAQAccordion s={s} />;
    case "location_cards": return <LocationCards s={s} locations={locations} />;
    case "doctor_profile": return <DoctorProfile s={s} />;
    case "news_grid": return <NewsGrid s={s} />;
    case "video_only": return <VideoOnlySection s={s} />;
    case "video_slider": return <VideoSliderSection s={s} />;
    case "image_gallery": return <ImageGallery s={s} />;
    case "stats_counter": return <StatsCounter s={s} />;
    case "story_list": return <StoryList s={s} />;
    case "glossary_list": return <GlossaryList s={s} />;
    case "custom_html": return <CustomHtmlEmbed s={s} />;
    case "html_block": return <HtmlBlock s={s} />;
    case "contact_form": return <ContactFormSection s={s} onSubmit={contactForm} />;
    case "spacer": return <div className="h-12 md:h-20" />;
    default: return null;
  }
}
